import type { Order, PaymentState, ProvisioningState } from './api';

/**
 * Customer-facing wording for the two order state machines.
 *
 * THE RULE: map, never invent. Every label here corresponds to exactly one real state
 * the server reported. No status is softened, merged, or made up to read better, and
 * nothing implies payment or provisioning that has not happened.
 *
 * Two machines run independently — payment (authority: the payment webhook, later) and
 * provisioning (authority: PowerPool, later) — so an order can legitimately be paid
 * while provisioning has not started. The headline uses the server's own projection;
 * the detail rows show each machine separately, because collapsing them would hide
 * exactly the combinations worth seeing.
 */

export type Tone = 'neutral' | 'pending' | 'positive' | 'negative';

export interface StatusPresentation {
  label: string;
  description: string;
  tone: Tone;
}

/** The server's combined projection: created | paid | provisioning | active | expired | failed | refunded. */
export const PROJECTED_STATUS: Record<string, StatusPresentation> = {
  created: {
    label: 'Awaiting payment',
    /*
     * SAYS WHAT HAPPENS NEXT, not merely what has not happened. The previous wording
     * ("No payment has been taken yet") was true, but the page went on to repeat it in a
     * standalone note and again in the Payment row, so a customer with an unpaid order
     * read the same fact three times and was told nothing about how to resolve it.
     *
     * "Your order is saved" is kept and kept first: it is the reassurance, and it is
     * what makes the following sentence an invitation rather than a demand.
     */
    description: 'Your order is saved. Complete payment to start your hashrate rental.',
    tone: 'pending',
  },
  paid: {
    label: 'Payment received',
    description: 'Your payment is confirmed. Setting up your hashrate is the next step.',
    tone: 'positive',
  },
  provisioning: {
    label: 'Setting up',
    description: 'Your hashrate allocation is being prepared.',
    tone: 'pending',
  },
  active: {
    label: 'Mining',
    description: 'Your hashrate is allocated and running for the rest of its term.',
    tone: 'positive',
  },
  /*
   * A RENTAL THAT RAN ITS COURSE. See PAYMENT_EXPIRED below for the other, entirely
   * different thing the server also projects as `expired`.
   */
  expired: {
    label: 'Ended',
    description: 'This order has reached the end of its term.',
    tone: 'neutral',
  },
  failed: {
    label: 'Not completed',
    description: 'This order was not completed. Nothing has been charged.',
    tone: 'negative',
  },
  refunded: {
    label: 'Refunded',
    description: 'This order was refunded.',
    tone: 'neutral',
  },
};

export const PAYMENT_STATE: Record<PaymentState, StatusPresentation> = {
  pending_payment: {
    label: 'Not paid yet',
    description: 'No payment has been taken.',
    tone: 'pending',
  },
  paid: { label: 'Paid', description: 'Payment confirmed.', tone: 'positive' },
  failed: { label: 'Payment failed', description: 'Payment did not go through.', tone: 'negative' },
  expired: { label: 'Expired', description: 'This order expired before payment.', tone: 'neutral' },
  refunded: { label: 'Refunded', description: 'This payment was refunded.', tone: 'neutral' },
};

export const PROVISIONING_STATE: Record<ProvisioningState, StatusPresentation> = {
  not_provisioned: {
    label: 'Not started',
    description: 'Setup begins once payment is confirmed.',
    tone: 'neutral',
  },
  provisioning: { label: 'In progress', description: 'Your allocation is being set up.', tone: 'pending' },
  active: { label: 'Running', description: 'Your hashrate is allocated.', tone: 'positive' },
  expired: { label: 'Ended', description: 'The rental term has finished.', tone: 'neutral' },
  // Deliberately not dressed up: something went wrong and somebody needs to look at it.
  provision_failed: {
    label: 'Needs attention',
    description: 'Setup did not complete. We are looking into it.',
    tone: 'negative',
  },
};

const UNKNOWN: StatusPresentation = {
  label: 'Unknown',
  description: 'We could not read the status of this order.',
  tone: 'neutral',
};

/**
 * A checkout that ran out of time before it was ever paid.
 *
 * NOT the same event as a rental reaching the end of its term, though the server
 * projects both as `expired`. Nothing was bought, nothing was charged, and no mining
 * ever happened, so the reassurance is the important half of the sentence.
 */
const PAYMENT_EXPIRED: StatusPresentation = {
  label: 'Payment expired',
  description: 'This order expired before payment was completed. Nothing was charged.',
  tone: 'neutral',
};

/**
 * The headline for an order.
 *
 * ===========================================================================
 * ONE PROJECTED WORD, TWO UNRELATED MEANINGS. The server's `expired` covers BOTH a
 * payment that expired before it was made AND a rental that finished its term. Read
 * alone it produced "Ended: this order has reached the end of its term" on an order
 * that was never paid and never mined a single share.
 *
 * The two machines are already on the DTO, so the ambiguity is resolved here rather
 * than by widening the API: if the PAYMENT machine expired, that is what happened, and
 * no term was ever running to end.
 */
export function describeOrder(order: Order): StatusPresentation {
  if (order.states?.payment === 'expired') return PAYMENT_EXPIRED;

  return PROJECTED_STATUS[order.status] ?? UNKNOWN;
}

export function describePayment(order: Order): StatusPresentation {
  return PAYMENT_STATE[order.states?.payment] ?? UNKNOWN;
}

export function describeProvisioning(order: Order): StatusPresentation {
  return PROVISIONING_STATE[order.states?.provisioning] ?? UNKNOWN;
}

/** True while nothing has been charged — used to keep that fact visible. */
export function isUnpaid(order: Order): boolean {
  return order.states?.payment === 'pending_payment';
}
